/**
 * The two plan-system illustrations' motion, as data plus one runner.
 *
 * ── Why this is JavaScript and not a keyframe block ──────────────────────────────────────────
 * The phone draws both loops in CSS and `docs/design/plan-propozycje-2026-09-21/plan.css` ports
 * them that way too, because a mockup has no preference to read. This app does:
 * `tokens/base.css`'s single `prefers-reduced-motion` block collapses a CSS duration to 0.001ms
 * and cannot reach a Web Animations API animation, and a LOOPING animation collapsed that way
 * repeats thousands of times a second — a strobe rather than a stillness. `CLAUDE.md` therefore
 * requires `reducedMotion` to arrive as a PARAMETER, which means Motion, which means the stop
 * tables have to be values something can pass in.
 *
 * ── The stops are the phone's, transcribed, and they are the contract ────────────────────────
 * One 5400ms period for both illustrations, the same percentages, `cubic-bezier(.55,0,.3,1)` on
 * every hop and on the cursor, `ease-in-out` on every mark, ring and label, and a 900ms linear
 * drift on the rest rules. A percentage below is a `times` entry, so the table can be read
 * against `plan.css` line for line.
 *
 * ── Two deviations from the CSS, both forced, both visual no-ops ─────────────────────────────
 * 1. **A border that changes colour is drawn as two stacked rings whose OPACITY crosses.**
 *    `seq-activeframe` and `seq-wakeborder` tween `border-color` between two tokens. Motion
 *    resolves a `var()` in a keyframe once, when the animation starts, so a theme switch under a
 *    running loop would leave it tweening between the previous theme's colours until something
 *    restarted it. Two rings under `opacity` carry their colours in CSS, where the theme class
 *    already governs them, and the drawn result is the same cross-fade.
 * 2. **The frame's travel is MEASURED, not `var(--step)`.** The CSS computes a column pitch from
 *    `100cqw`; here the pitch is read off the rendered tiles, which is the same number and is also
 *    correct for the projection calendar, whose columns are not equal-width by construction.
 *
 * ── Parking on the LAST frame ────────────────────────────────────────────────────────────────
 * `prefers-reduced-motion` parks both loops where the phone parks them: every mark landed, the
 * cursor on day two. That is a deliberate choice about what a still viewer should SEE — the first
 * frame of either loop is an empty week and an unlit cursor, which reads as a broken drawing.
 *
 * No React here. The components own the elements and hand them in.
 */

import { animate, type Easing } from 'motion/react';

/** The phone's loop period, in seconds — `--loop: 5400ms`. */
export const LOOP_SECONDS = 5.4;

/** The drift on a rest rule: 11px of dashes, 900ms, linear, forever. */
export const DRIFT_SECONDS = 0.9;

/** The dash pitch the drift travels, in pixels — `repeating-linear-gradient(… 0 5px, transparent 5px 11px)`. */
export const DRIFT_PITCH = 11;

/** `cubic-bezier(.55,0,.3,1)` — the phone's `--hop`. Every frame jump and the cursor. */
export const HOP_EASE: Easing = [0.55, 0, 0.3, 1];

/** `ease-in-out` — the phone's `--soft`. Every mark, ring and label. */
const SOFT: Easing = 'easeInOut';
const HOLD: Easing = 'linear';

/** One property's whole loop: the values, where they land, and the curve into each one. */
export interface LoopTrack {
  /** Keyed by a Motion animatable property — `opacity`, `scale`, `x`, `backgroundPositionX`. */
  readonly keyframes: Readonly<Record<string, readonly number[]>>;
  /** Fractions of the period, ascending, first `0` and last `1`. */
  readonly times: readonly number[];
  /** One curve per SEGMENT, so this is one shorter than `times`. */
  readonly ease: readonly Easing[];
  /** Seconds. Every track but the drift runs the whole loop. */
  readonly duration: number;
}

/**
 * A mark popping in: hidden, then over-shot to 1.18, then settled.
 *
 * Four of them, 18% apart — done 4/11/17, miss 22/29/35, free 40/47/53, due 56/63/69 — which is
 * what makes the week read as filling in rather than as appearing.
 */
export function markTrack(from: number, peak: number, to: number): LoopTrack {
  return {
    keyframes: { opacity: [0, 0, 1, 1, 1], scale: [0.4, 0.4, 1.18, 1, 1] },
    times: [0, from, peak, to, 1],
    ease: [HOLD, SOFT, SOFT, HOLD],
    duration: LOOP_SECONDS,
  };
}

/** The four marks of the calendar week, in the order the illustration lands them. */
export const CAL_MARK_STOPS = {
  done: markTrack(0.04, 0.11, 0.17),
  miss: markTrack(0.22, 0.29, 0.35),
  free: markTrack(0.4, 0.47, 0.53),
  due: markTrack(0.56, 0.63, 0.69),
} as const;

/** The missed tile dimming behind its mark — `cal-missfade`, 36 → 52%. */
export const CAL_MISS_FADE: LoopTrack = {
  keyframes: { opacity: [1, 1, 0.5, 0.5] },
  times: [0, 0.36, 0.52, 1],
  ease: [HOLD, SOFT, HOLD],
  duration: LOOP_SECONDS,
};

/**
 * The frame hopping along a row of columns — `cal-hop` and `mcal-hop` are the same walk.
 *
 * `stops` is where the frame stands, in pixels from its starting column, and it is TRUNCATED to
 * however many columns the row actually has: three programmed days hop, hop, hold; two hop and
 * hold; one never moves at all. The mock's `mcal-hop-two` is that truncation written out by hand.
 */
export function hopTrack(stops: readonly number[]): LoopTrack {
  /* Hold, jump, hold, jump, hold, jump, rest — the phone's 0/10/18/28/36/46/54. A row with fewer
   * columns keeps the same rhythm and simply stops early, holding its last column to the end. */
  const schedule = [
    { at: 0, ease: HOLD },
    { at: 0.1, ease: HOP_EASE },
    { at: 0.18, ease: HOLD },
    { at: 0.28, ease: HOP_EASE },
    { at: 0.36, ease: HOLD },
    { at: 0.46, ease: HOP_EASE },
    { at: 0.54, ease: HOLD },
  ];

  const x: number[] = [];
  const times: number[] = [];
  const ease: Easing[] = [];
  const last = stops[stops.length - 1] ?? 0;

  for (const [index, { at, ease: curve }] of schedule.entries()) {
    // Two schedule entries per column — the moment it is reached and the moment it is left.
    const column = Math.min(Math.floor(index / 2), stops.length - 1);
    x.push(stops[column] ?? last);
    times.push(at);
    ease.push(curve);
  }

  x.push(last);
  times.push(1);

  return { keyframes: { x }, times, ease, duration: LOOP_SECONDS };
}

/** The cursor crossing from day one to day two — `seq-cursor`, 26 → 42%. */
export function cursorTrack(travel: number): LoopTrack {
  return {
    keyframes: { x: [0, 0, travel, travel] },
    times: [0, 0.26, 0.42, 1],
    ease: [HOLD, HOP_EASE, HOLD],
    duration: LOOP_SECONDS,
  };
}

/** Day one's tick landing — `seq-check`, 28/38/46%. */
export const SEQ_CHECK: LoopTrack = markTrack(0.28, 0.38, 0.46);

/** Day one's accent ring letting go — `seq-activeframe`, 28 → 42%. */
export const SEQ_ACTIVE_RING: LoopTrack = {
  keyframes: { opacity: [1, 1, 0, 0] },
  times: [0, 0.28, 0.42, 1],
  ease: [HOLD, SOFT, HOLD],
  duration: LOOP_SECONDS,
};

/** Day two's ink ring waking — `seq-wakeborder`, 32 → 46%. */
export const SEQ_WAKE_RING: LoopTrack = {
  keyframes: { opacity: [0, 0, 1, 1] },
  times: [0, 0.32, 0.46, 1],
  ease: [HOLD, SOFT, HOLD],
  duration: LOOP_SECONDS,
};

/** The halo pulsing once around day two — `seq-ring`, 32/46/60%. */
export const SEQ_HALO: LoopTrack = {
  keyframes: { opacity: [0, 0, 1, 0, 0] },
  times: [0, 0.32, 0.46, 0.6, 1],
  ease: [HOLD, SOFT, SOFT, HOLD],
  duration: LOOP_SECONDS,
};

/** "Pull" leaving — `seq-lblout`, 30 → 42%. */
export const SEQ_LABEL_OUT: LoopTrack = {
  keyframes: { opacity: [1, 1, 0, 0] },
  times: [0, 0.3, 0.42, 1],
  ease: [HOLD, SOFT, HOLD],
  duration: LOOP_SECONDS,
};

/** "czeka" arriving — `seq-lblin`, 32 → 46%. */
export const SEQ_LABEL_IN: LoopTrack = {
  keyframes: { opacity: [0, 0, 1, 1] },
  times: [0, 0.32, 0.46, 1],
  ease: [HOLD, SOFT, HOLD],
  duration: LOOP_SECONDS,
};

/** The dashes drifting under a rest label — `seq-drift`, 900ms, linear, its own period. */
export const SEQ_DRIFT: LoopTrack = {
  keyframes: { backgroundPositionX: [0, DRIFT_PITCH] },
  times: [0, 1],
  ease: [HOLD],
  duration: DRIFT_SECONDS,
};

/** One track bound to the elements that play it. */
export interface LoopBinding {
  readonly elements: readonly (HTMLElement | null)[];
  readonly track: LoopTrack;
}

/**
 * The frame a still viewer sees: every track's LAST value, written as an inline style.
 *
 * `x` and `scale` are composed by hand because they are Motion's own shorthands rather than CSS
 * properties, and nothing is running to compose them.
 */
function park(element: HTMLElement, track: LoopTrack): void {
  const lastOf = (key: string): number | undefined => {
    const values = track.keyframes[key];
    return values?.[values.length - 1];
  };

  const opacity = lastOf('opacity');
  if (opacity !== undefined) element.style.opacity = String(opacity);

  const x = lastOf('x');
  const scale = lastOf('scale');
  const transform = [
    x === undefined ? '' : `translateX(${String(x)}px)`,
    scale === undefined ? '' : `scale(${String(scale)})`,
  ]
    .filter(Boolean)
    .join(' ');
  if (transform !== '') element.style.transform = transform;

  const drift = lastOf('backgroundPositionX');
  if (drift !== undefined) element.style.backgroundPositionX = `${String(drift)}px`;
}

/**
 * Start every track, or park every track. Returns the cleanup that stops and un-styles them.
 *
 * Motion leaves its last keyframe on the element as an inline style, and a leftover `transform` —
 * even `translateX(0px)` — makes the element a containing block for anything `position: fixed`
 * inside it, so the properties are taken back off on the way out. Same rule `useDayStepMotion`
 * writes out at length.
 */
export function runPlanSystemLoop(
  bindings: readonly LoopBinding[],
  reducedMotion: boolean,
): () => void {
  const touched: HTMLElement[] = [];
  const playing: { stop: () => void }[] = [];

  for (const { elements, track } of bindings) {
    const present = elements.filter((element): element is HTMLElement => element !== null);
    if (present.length === 0) continue;
    touched.push(...present);

    if (reducedMotion) {
      for (const element of present) park(element, track);
      continue;
    }

    playing.push(
      animate(present, track.keyframes, {
        duration: track.duration,
        times: [...track.times],
        ease: [...track.ease],
        repeat: Infinity,
      }),
    );
  }

  return () => {
    for (const animation of playing) animation.stop();
    for (const element of touched) {
      element.style.removeProperty('opacity');
      element.style.removeProperty('transform');
      element.style.removeProperty('background-position-x');
    }
  };
}
